import { Component, inject, Input } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { CustomCodeFormShape, CustomCodeVariableDoc } from '../custom-code/custom-code.model';
import { GlobalSettingsFormShape } from '../../components/smart-cart-form-global-settings/global-settings.model';
import { SmartCartPreviewStateService } from '../../preview/smart-cart-preview-state.service';
import { TimerPlacementOption } from '../../components/timer-config/timer-config.component';
import { TimerFormShape, TimerPreviewState } from '../../components/timer-config/timer.model';
import { UpsellItemsFormShape, UPSELL_ITEMS_STARTER_TEMPLATE, UPSELL_ITEMS_VARIABLES } from './upsell-items.model';
import { readDisplayConditionsGroup } from '../../components/smart-cart-form-display-conditions/display-conditions.model';

/**
 * Upsell Items widget editor (tabbed shell): Product Selection / Timer.
 * The Goals + Exit intent tabs (other dev) slot into `tabs` alongside these.
 * (Settings tab is out of scope for this ticket — discounts deferred — so it's not shown.)
 */
type UpsellEditorTab = 'product-selection' | 'timer' | 'display-conditions';

@Component({
  selector: 'smart-cart-form-upsell-items-editor',
  templateUrl: './upsell-items.component.html',
  styleUrls: ['./upsell-items.component.scss'],
  standalone: false,
})
export class SmartCartFormUpsellItemsEditorComponent {
  @Input({ required: true }) formGroup!: FormGroup<UpsellItemsFormShape>;
  /** Global Button Design settings, forwarded to Product Selection for the button "Reset to global color". */
  @Input({ required: true }) globalSettingsFormGroup!: FormGroup<GlobalSettingsFormShape>;
  /** Forwarded to the shared timer for icon upload (needs a saved cart + the widget's usage token). */
  @Input() usageToken = '';
  @Input() smartCartId: number | null = null;

  readonly tabs: { label: string; value: UpsellEditorTab }[] = [
    { label: 'Product Selection', value: 'product-selection' },
    { label: 'Timer', value: 'timer' },
    { label: 'Display Conditions', value: 'display-conditions' },
  ];
  activeTab: UpsellEditorTab = 'product-selection';

  get productSelectionGroup(): UntypedFormGroup {
    return this.formGroup.controls.productSelection as UntypedFormGroup;
  }

  get displayConditionsGroup(): UntypedFormGroup | null {
    return readDisplayConditionsGroup(this.formGroup);
  }

  get customCodeGroup(): FormGroup<CustomCodeFormShape> {
    return this.formGroup.controls.settings.controls.customCode;
  }

  get timerGroup(): FormGroup<TimerFormShape> {
    return this.formGroup.controls.timer;
  }

  readonly customCodeStarter = UPSELL_ITEMS_STARTER_TEMPLATE;
  readonly customCodeVariables: CustomCodeVariableDoc[] = UPSELL_ITEMS_VARIABLES;

  readonly timerPlacementOptions: TimerPlacementOption[] = [
    { value: 'inline', text: 'Inline (full width bar)' },
    { value: 'badge', text: 'Badge (icon+text)' },
    { value: 'in_title', text: 'In Title' },
  ];

  private readonly previewState = inject(SmartCartPreviewStateService);
  readonly timerPreviewState = this.previewState.upsellPreviewTimerState;

  setTimerPreview(state: TimerPreviewState): void {
    this.previewState.setUpsellPreviewTimerState(state);
  }

  onTabChange(tab: UpsellEditorTab): void {
    this.activeTab = tab;
  }
}
